import { useCallback, useMemo } from "react";
import { useClimbersProgress } from "./useClimbersProgress";
import type {
  ClimberKeyInput,
  LadderProgress,
  ClimberFacets,
  ProfileChoices,
  CareerPlanSummary,
  CoursePlanSummary,
  CareerClimbProgress,
} from "../types/mmp-graphql";

interface ClimberLike {
  id: string;
  path?: string | null;
  parentID?: string | null;
  ladderProgress?: LadderProgress[];
  facets?: ClimberFacets[];
  profileChoices?: ProfileChoices;
  careerPlan?: CareerPlanSummary;
  coursePlan?: CoursePlanSummary;
  shoutOutCount?: number;
  careerClimbs?: CareerClimbProgress[];
}

// districtId is the dis- segment of the climber's path; fall back to parentID
// (climbers are parented to their district) when a path isn't available.
export const extractDistrictId = (
  climber: Pick<ClimberLike, "path" | "parentID">,
): string | undefined => {
  const fromPath = climber.path?.match(/dis-[^#]+/)?.[0];
  if (fromPath) return fromPath;
  return climber.parentID?.startsWith("dis-") ? climber.parentID : undefined;
};

/**
 * Loads prg- progress records for a roster of climbers and returns a merge
 * helper that overlays the migrated progress fields onto a climber. The prg-
 * record is preferred, with the climber record kept as a fallback for climbers
 * not yet migrated. Used by teacher/staff roster views so they read progress
 * (ladders, facets, profile choices, career/course plans) from the prg- record.
 */
export const useClimbersWithProgress = (
  climbers: ClimberLike[] | undefined,
) => {
  const keys: ClimberKeyInput[] = useMemo(
    () =>
      (climbers ?? []).flatMap((climber) => {
        const districtId = extractDistrictId(climber);
        return districtId ? [{ climberId: climber.id, districtId }] : [];
      }),
    [climbers],
  );

  const { progressByClimberId, loading } = useClimbersProgress(keys);

  const mergeProgress = useCallback(
    <T extends ClimberLike>(climber: T): T => {
      const progress = progressByClimberId.get(climber.id);
      if (!progress) return climber;
      return {
        ...climber,
        ladderProgress: progress.ladderProgress ?? climber.ladderProgress,
        facets: progress.facets ?? climber.facets,
        profileChoices: progress.profileChoices ?? climber.profileChoices,
        careerPlan: progress.careerPlan ?? climber.careerPlan,
        coursePlan: progress.coursePlan ?? climber.coursePlan,
        shoutOutCount: progress.shoutOutCount ?? climber.shoutOutCount,
        careerClimbs: progress.careerClimbs ?? climber.careerClimbs,
      } as T;
    },
    [progressByClimberId],
  );

  // Pre-merged roster for the common case; mergeProgress stays exported for
  // callers that need to merge a single record.
  const climbersWithProgress = useMemo(
    () => (climbers ?? []).map(mergeProgress),
    [climbers, mergeProgress],
  );

  return {
    climbers: climbersWithProgress,
    mergeProgress,
    progressLoading: loading,
  };
};
